import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthSuccessSplash from '../components/AuthSuccessSplash';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles the return hop from Emergent-managed Google auth.
 *
 * Emergent redirects the user to `<redirect>#session_id=<uuid>`. We read
 * that fragment (via `useLocation().hash`, per the playbook - NOT
 * window.location.hash - so we stay reactive), POST it to our backend
 * which swaps it for a real user + sets our `outlaw_session` cookie,
 * refresh AuthContext, then show the shared `AuthSuccessSplash` so the
 * reader sees a big green "You're in" confirmation before we route them
 * to /subscribe (unpaid) or /account (paid).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH - the login button that kicks this off must
 * always use `window.location.origin`.
 */
const AuthGoogleCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const hasProcessed = useRef(false);
  const [phase, setPhase] = useState('exchanging'); // exchanging | ok
  const [email, setEmail] = useState('');

  useEffect(() => {
    // StrictMode calls this twice - guard synchronously with a ref.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const raw = location.hash || '';
    const match = raw.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';

    (async () => {
      if (!sessionId) {
        navigate('/login?err=missing_session', { replace: true });
        return;
      }
      try {
        const res = await fetch(`${API}/api/auth/google/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Session-ID': sessionId },
        });
        const data = await res.json().catch(() => ({}));
        // Wipe the hash so a refresh doesn't loop.
        try { window.history.replaceState({}, '', window.location.pathname); } catch {}
        if (!res.ok) {
          const msg = encodeURIComponent(data.detail || 'Google sign-in failed.');
          navigate(`/login?err=${msg}`, { replace: true });
          return;
        }
        setEmail(data.email || '');
        await refresh();
        setPhase('ok');
      } catch (e) {
        navigate(`/login?err=${encodeURIComponent(e.message || 'Network error.')}`, { replace: true });
      }
    })();
  }, [location.hash, navigate, refresh]);

  if (phase === 'ok') {
    return (
      <AuthSuccessSplash
        testId="google-auth-success"
        email={email || user?.email}
        subline="Taking you to your pricing plans…"
        onDone={() => {
          const dest = user && user.plan && user.plan !== 'free' ? '/account?welcome=1' : '/subscribe?welcome=1';
          navigate(dest, { replace: true });
        }}
      />
    );
  }

  return (
    <div
      data-testid="google-auth-callback"
      className="min-h-screen bg-black text-white flex items-center justify-center"
    >
      <div className="text-center">
        <div className="inline-block h-6 w-6 rounded-full border-2 border-red-500 border-t-transparent animate-spin mb-3" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-red-400 font-black">
          Signing you in…
        </p>
      </div>
    </div>
  );
};

export default AuthGoogleCallback;
